import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { toast } from '../../components/toasts';
import { Button, ErrorText, Field, Modal, Spinner, Textarea } from '../../components/ui';
import { readImages, suggestTicket } from '../../lib/ai';
import { get, post, put } from '../../lib/api';
import { contactLabel } from '../../lib/format';
import { useAttendanceSettings } from '../../lib/queries';
import { TakeContext } from '../../lib/types';
import { Customer, CustomerPicker, decisionBody, decisionError, TicketChoice, TicketChoiceValue } from './TicketFields';

/**
 * Cliente + chamado (vincular um aberto, abrir um novo ou decidir depois) de um atendimento já
 * assumido. Usado no "Definir chamado" e na primeira etapa de "Finalizar" quando falta o chamado.
 */
export function TicketDecisionForm({
  attendanceId,
  allowDeferred = true,
  pendingSummary,
  onSaved,
  renderActions,
}: {
  attendanceId: string;
  allowDeferred?: boolean;
  /** Atendimento já finalizado com "decidir depois": o resumo ainda pode ser revisto (texto atual). */
  pendingSummary?: string | null;
  onSaved: (mode: TicketChoiceValue['mode']) => void;
  renderActions: (save: () => void, saving: boolean, ready: boolean) => ReactNode;
}) {
  const qc = useQueryClient();
  const ctx = useQuery({ queryKey: ['take-context', attendanceId], queryFn: () => get<TakeContext>(`/attendances/${attendanceId}/take-context`), staleTime: 0 });
  const [customer, setCustomer] = useState<Customer | null>(null);
  const ai = useAttendanceSettings().data?.ai;
  const aiEnabled = Boolean(ai?.ticketSuggestion);
  const withSummary = pendingSummary !== undefined;
  const [summary, setSummary] = useState(pendingSummary ?? '');
  const [generating, setGenerating] = useState(false);
  const [choice, setChoice] = useState<TicketChoiceValue>({ mode: 'create', serviceId: '', title: '' });
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const generateSummary = async () => {
    if (summary.trim() && !window.confirm('Substituir o resumo atual pelo da IA?')) return;
    setGenerating(true);
    setError(null);
    try {
      if (ai?.imageReading) await readImages(attendanceId);
      setSummary((await post<{ summary: string }>(`/attendances/${attendanceId}/ai-summary`)).summary);
    } catch (err) {
      setError(err);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    const d = ctx.data;
    if (!d) return;
    setCustomer(d.contact.agidesk.customer);
    setChoice(d.openTickets?.length ? { mode: 'reuse', ticketId: d.openTickets[0].id } : { mode: 'create', serviceId: '', title: defaultTitle(d) });
  }, [ctx.data]);

  const d = ctx.data;
  const save = async () => {
    if (!allowDeferred && choice.mode === 'deferred') return setError(new Error('Escolha um chamado aberto ou abra um novo'));
    const invalid = decisionError(choice, customer);
    if (invalid) return setError(new Error(invalid));
    setSaving(true);
    setError(null);
    try {
      if ((customer?.id ?? null) !== (d!.contact.agidesk.customer?.id ?? null)) await put(`/contacts/${d!.contact.id}/customer`, { customerId: customer?.id ?? null });
      await post(`/attendances/${attendanceId}/ticket`, { ...decisionBody(choice), ...(withSummary ? { summary: summary.trim() } : {}) });
      void qc.invalidateQueries({ queryKey: ['attendance', attendanceId] });
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      onSaved(choice.mode);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {ctx.isLoading && <Spinner />}
      <ErrorText error={ctx.error} />
      {d && (
        <>
          <h3 className="mb-2 text-sm font-semibold">Cliente (empresa)</h3>
          <div className="mb-4">
            <CustomerPicker contact={d.contact} value={customer} onChange={setCustomer} />
          </div>
          <h3 className="mb-2 text-sm font-semibold">Chamado</h3>
          <TicketChoice
            value={choice}
            onChange={setChoice}
            openTickets={d.openTickets}
            services={d.services}
            agideskAvailable={d.agideskAvailable}
            defaultTitle={defaultTitle(d)}
            allowDeferred={allowDeferred || !d.agideskAvailable}
            onSuggest={aiEnabled ? () => suggestTicket(attendanceId, customer?.id) : undefined}
          />
          {withSummary && (
            <div className="mt-4">
              <Field label="Resumo" hint="(vai para o registro interno no Agidesk junto com o chamado)">
                <Textarea rows={summary.length > 200 ? 7 : 4} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={5000} placeholder="O que foi tratado e como ficou" />
              </Field>
              {ai?.summary && (
                <div className="-mt-2 flex items-center gap-2">
                  <Button size="sm" variant="ghost" loading={generating} onClick={() => void generateSummary()} title="A IA lê a conversa e as anotações e sugere o resumo; revise antes de salvar">
                    <Sparkles className="size-3.5" /> Gerar resumo com IA
                  </Button>
                  <span className="text-[11px] text-fg-soft">Sugestão: revise e corrija antes de salvar.</span>
                </div>
              )}
            </div>
          )}
          <ErrorText error={error} />
        </>
      )}
      {renderActions(() => void save(), saving, Boolean(d))}
    </>
  );
}

const defaultTitle = (d: TakeContext) => `Atendimento WhatsApp - ${contactLabel({ contactName: d.contact.name, contactPhone: d.contact.phones[0] ?? null })}`;

/** Definir (ou corrigir) o chamado depois de assumir, inclusive após "decidir depois". */
export function TicketDecisionModal({ attendanceId, pendingSummary, onClose }: { attendanceId: string; pendingSummary?: string | null; onClose: () => void }) {
  return (
    <Modal open title="Definir chamado" onClose={onClose} width="max-w-2xl">
      <TicketDecisionForm
        attendanceId={attendanceId}
        pendingSummary={pendingSummary}
        onSaved={() => {
          toast('Chamado definido', 'ok');
          onClose();
        }}
        renderActions={(save, saving, ready) => (
          <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
            <Button onClick={onClose}>Cancelar</Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={!ready}>
              Salvar
            </Button>
          </div>
        )}
      />
    </Modal>
  );
}